import { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { App, Drawer, Table, Button, Space, InputNumber, Select, Image, Typography, Divider } from 'antd';
import dayjs from 'dayjs';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrayMoveImmutable } from 'array-move';
import WorkExperiences from '@/components/common/WorkExperiences';
import { convertDateTimeRangeToLocalText } from '@utils/utils';
import { DESK_MODE_OPTIONS, DeskModeRuleContent, formatDeskModeRuleText } from './deskModeConstants';
import { getPostCodeOrderMap, computeAutoSort } from './expertSortUtils';
import './style.less';
import { updateHallExpertSortsApi, getHallExpertSortsApi } from '@services/Agenda';

/** 拖拽手柄 context，将 listeners/setActivatorNodeRef 从行传给手柄单元格 */
const DragHandleContext = createContext({});

function DragHandle() {
    const { setActivatorNodeRef, listeners } = useContext(DragHandleContext);
    return (
        <Button type="text" size="small" ref={setActivatorNodeRef} style={{ cursor: 'grab', touchAction: 'none' }} {...listeners}>
            <i className="bi bi-grip-vertical" />
        </Button>
    );
}

function SortableTableRow({ children, ...props }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: String(props['data-row-key']),
    });
    const context = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
    const style = {
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <DragHandleContext.Provider value={context}>
            <tr {...props} ref={setNodeRef} style={style} {...attributes}>
                {children}
            </tr>
        </DragHandleContext.Provider>
    );
}

const { Text } = Typography;

const DEMO_IMAGES = {
    u_shape: {
        type: 'single',
        src: 'https://mediafiles.cn-healthcare.com/assets/conf_seat/seat_demo_u_shape.jpg',
    },
    school_desk: {
        type: 'dual',
        odd: 'https://mediafiles.cn-healthcare.com/assets/conf_seat/seat_demo_shcool_desk_odd.jpg',
        even: 'https://mediafiles.cn-healthcare.com/assets/conf_seat/seat_demo_shcool_desk_even.jpg',
    },
    long_table: {
        type: 'dual',
        odd: 'https://mediafiles.cn-healthcare.com/assets/conf_seat/seat_demo_long_table_odd.jpg',
        even: 'https://mediafiles.cn-healthcare.com/assets/conf_seat/seat_demo_long_table_even.jpg',
    },
};

const mapExpertsToTableData = (experts = []) =>
    experts.map((expert, index) => ({
        key: expert.id || index,
        expertId: expert.id,
        expertName: expert.memUsername,
        workExperiences: expert.ssoMemberWorkExperiencesEntities || [],
        sort: expert.sort || '',
        expertLevel: expert.expertLevel,
        namePinyin: expert.namePinyin || '',
    }));

const SeatingArrangementModal = ({ open, onCancel, currentConferenceHall, getConferenceList, agendasData = [] }) => {
    const { message: messageApi, modal } = App.useApp();
    const [tableData, setTableData] = useState([]);
    const [deskMode, setDeskMode] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const postCodeOrderMapRef = useRef(new Map());

    // 组件挂载时预加载字典
    useEffect(() => {
        getPostCodeOrderMap().then(map => {
            postCodeOrderMapRef.current = map;
        });
    }, []);

    useEffect(() => {
        if (open && currentConferenceHall?.id) {
            loadExpertSorts();
        }
    }, [open, currentConferenceHall?.id]);

    const loadExpertSorts = async () => {
        try {
            setLoading(true);
            const res = await getHallExpertSortsApi({ hallId: currentConferenceHall.id });
            if (res.status_code === 200 && res.data) {
                let sortedExperts = [];
                let nextDeskMode = '';

                if (Array.isArray(res.data)) {
                    sortedExperts = res.data;
                } else {
                    sortedExperts = res.data.sortedExperts || [];
                    nextDeskMode = res.data.deskMode || '';
                }

                setDeskMode(nextDeskMode);
                setTableData(mapExpertsToTableData(sortedExperts));
                setIsDirty(false);
            }
        } catch (error) {
            console.error('加载专家排序数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 处理按姓名拼音排序
    const handleSortByPinyin = () => {
        const hasAnySort = tableData.some(item => item.sort !== '' && item.sort !== null && item.sort !== undefined);
        const doSort = () => {
            const sorted = [...tableData].sort((a, b) => {
                const pa = a.namePinyin || '\uffff';
                const pb = b.namePinyin || '\uffff';
                if (pa < pb) return -1;
                if (pa > pb) return 1;
                return 0;
            });
            setTableData(sorted.map((item, index) => ({ ...item, sort: index + 1 })));
            setIsDirty(true);
        };
        if (hasAnySort) {
            modal.confirm({
                title: '按姓名拼音排序',
                content: '按姓名拼音排序会覆盖原有排序顺序，确定继续？',
                centered: true,
                okText: '确定',
                cancelText: '取消',
                onOk: doSort,
            });
        } else {
            doSort();
        }
    };

    // 处理按日程时间排序
    const handleSortByAgendaTime = () => {
        const hasAnySort = tableData.some(item => item.sort !== '' && item.sort !== null && item.sort !== undefined);
        const doSort = () => {
            // 构建 expertId -> 最早 startTime 的 Map
            const expertTimeMap = new Map();
            agendasData.forEach(agenda => {
                const startTime = agenda.startTime;
                if (!startTime) return;
                (agenda.guestList || []).forEach(guest => {
                    const id = guest.id;
                    if (!expertTimeMap.has(id) || startTime < expertTimeMap.get(id)) {
                        expertTimeMap.set(id, startTime);
                    }
                });
            });
            const sorted = [...tableData].sort((a, b) => {
                const ta = expertTimeMap.get(a.expertId);
                const tb = expertTimeMap.get(b.expertId);
                if (!ta && !tb) {
                    const pa = a.namePinyin || '\uffff';
                    const pb = b.namePinyin || '\uffff';
                    if (pa < pb) return -1;
                    if (pa > pb) return 1;
                    return 0;
                }
                if (!ta) return 1;
                if (!tb) return -1;
                if (ta < tb) return -1;
                if (ta > tb) return 1;
                const pa = a.namePinyin || '\uffff';
                const pb = b.namePinyin || '\uffff';
                if (pa < pb) return -1;
                if (pa > pb) return 1;
                return 0;
            });
            setTableData(sorted.map((item, index) => ({ ...item, sort: index + 1 })));
            setIsDirty(true);
        };
        if (hasAnySort) {
            modal.confirm({
                title: '按日程时间排序',
                content: '按日程时间排序会覆盖原有排序顺序，确定继续？',
                centered: true,
                okText: '确定',
                cancelText: '取消',
                onOk: doSort,
            });
        } else {
            doSort();
        }
    };

    // 处理自动排序
    const handleAutoSort = () => {
        const hasAnySort = tableData.some(item => item.sort !== '' && item.sort !== null && item.sort !== undefined);
        const doSort = () => {
            const sorted = computeAutoSort(tableData, postCodeOrderMapRef.current);
            setTableData(sorted);
            setIsDirty(true);
        };
        if (hasAnySort) {
            modal.confirm({
                title: '系统自动排序',
                content: '系统自动排序会覆盖原有排序顺序，确定系统自动排序？',
                centered: true,
                okText: '确定',
                cancelText: '取消',
                onOk: doSort,
            });
        } else {
            doSort();
        }
    };

    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // 拖拽结束：按新顺序重新编号 sort
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setTableData(prev => {
            const oldIndex = prev.findIndex(item => String(item.key) === active.id);
            const newIndex = prev.findIndex(item => String(item.key) === over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            const moved = arrayMoveImmutable(prev, oldIndex, newIndex);
            // 对有 sort 值的行按新顺序重新赋连续整数，无 sort 值的行保持在末尾
            const withSort = moved.filter(item => item.sort !== '' && item.sort !== null && item.sort !== undefined);
            const withoutSort = moved.filter(item => item.sort === '' || item.sort === null || item.sort === undefined);
            const reIndexed = withSort.map((item, i) => ({ ...item, sort: i + 1 }));
            return [...reIndexed, ...withoutSort];
        });
        setIsDirty(true);
    };

    // 处理排序顺序变更
    const handleSortOrderChange = (value, key) => {
        if (value !== undefined && value !== null && value < 0) {
            return;
        }
        const newData = tableData.map(item => (item.key === key ? { ...item, sort: value } : item));
        setTableData(newData);
        setIsDirty(true);
    };

    // 处理排序顺序失焦
    const handleSortBlur = () => {
        setTableData(prev => {
            const withSort = prev.filter(item => item.sort !== '' && item.sort !== null && item.sort !== undefined);
            const withoutSort = prev.filter(item => item.sort === '' || item.sort === null || item.sort === undefined);
            // 按数値升序排列后重新赋定连续整数
            withSort.sort((a, b) => Number(a.sort) - Number(b.sort));
            const reIndexed = withSort.map((item, i) => ({ ...item, sort: i + 1 }));
            return [...reIndexed, ...withoutSort];
        });
    };

    const columns = [
        {
            key: 'drag-handle',
            width: 40,
            align: 'center',
            render: () => <DragHandle />,
        },
        {
            title: '排序',
            dataIndex: 'sort',
            key: 'seq',
            width: 100,
            align: 'center',
            render: (_text, record) => (
                <InputNumber
                    value={record.sort}
                    onChange={value => handleSortOrderChange(value, record.key)}
                    onBlur={handleSortBlur}
                    min={0}
                    style={{ width: '100%' }}
                    parser={value => {
                        if (!value) return undefined;
                        const num = parseFloat(value);
                        return num < 0 ? undefined : num;
                    }}
                />
            ),
        },
        {
            title: '专家',
            dataIndex: 'expertName',
            key: 'expertName',
            width: 150,
        },
        {
            title: '单位任职',
            dataIndex: 'workExperiences',
            key: 'workExperiences',
            flex: 1,
            render: workExperiences => <WorkExperiences workExperiences={workExperiences} layout="inline" showSerialNumber={false} emptyText="–" />,
        },
    ];

    // 处理保存排序
    const handleSaveSort = async () => {
        try {
            setLoading(true);
            const hallExpertSorts = tableData.map(item => {
                const newItem = { id: item.expertId };
                if (item.sort) {
                    newItem.sort = item.sort;
                }
                return newItem;
            });
            const params = {
                id: currentConferenceHall.id,
                deskMode: deskMode || '',
                hallExpertSorts,
            };

            const res = await updateHallExpertSortsApi(params);
            if (res?.status_code === 200 || res?.code === 200) {
                messageApi.success('排序保存成功');
                setIsDirty(false);
                loadExpertSorts();
                getConferenceList();
            }
        } catch (error) {
            console.error('保存排序数据失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 备用复制方法
    const fallbackCopyTextToClipboard = text => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.width = '2em';
        textarea.style.height = '2em';
        textarea.style.padding = '0';
        textarea.style.border = 'none';
        textarea.style.outline = 'none';
        textarea.style.boxShadow = 'none';
        textarea.style.background = 'transparent';
        textarea.style.opacity = '0';
        textarea.setAttribute('readonly', '');
        const drawerContainer = document.querySelector('.seating-arrangement-drawer .ant-drawer-content') || document.body;
        drawerContainer.appendChild(textarea);

        let successful = false;
        try {
            textarea.focus();
            textarea.select();
            textarea.setSelectionRange(0, text.length);
            successful = document.execCommand('copy');
        } catch (err) {
            console.error('execCommand 复制错误:', err);
        } finally {
            drawerContainer.removeChild(textarea);
        }
        return successful;
    };

    // 获取格式化后的时间段
    const getFormattedTimeRange = () => {
        if (!currentConferenceHall?.agendasTime) return '';
        const dateTimesArr = currentConferenceHall.agendasTime.split(/-(?=\d{4})/) || [];
        if (dateTimesArr.length === 2) {
            return convertDateTimeRangeToLocalText(dateTimesArr[0], dateTimesArr[1], 0);
        }
        return currentConferenceHall.agendasTime;
    };

    // 处理复制专家姓名排序名单（按姓名拼音排序）
    const handleCopyNameList = async () => {
        const ordered = [...tableData].sort((a, b) => {
            const pa = a.namePinyin || '\uffff';
            const pb = b.namePinyin || '\uffff';
            if (pa < pb) return -1;
            if (pa > pb) return 1;
            return 0;
        });
        const text = ordered
            .map((item, index) => {
                const unitText =
                    item.workExperiences && item.workExperiences.length > 0
                        ? item.workExperiences
                              .map(exp => {
                                  const parts = [];
                                  if (exp.unitName && exp.unitName !== 'null') parts.push(exp.unitName);
                                  if (exp.unitOffice && exp.unitOffice !== 'null') parts.push(exp.unitOffice);
                                  if (exp.unitPost && exp.unitPost !== 'null') parts.push(exp.unitPost);
                                  return parts.join('');
                              })
                              .filter(Boolean)
                              .join('、')
                        : '–';
                return `${index + 1}\t${item.expertName}\t${unitText}`;
            })
            .join('\n');
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                messageApi.success('已复制专家姓名排序名单');
                return;
            }
        } catch (err) {
            console.warn('Clipboard API 复制失败，降级使用 execCommand：', err);
        }
        if (fallbackCopyTextToClipboard(text)) {
            messageApi.success('已复制专家姓名排序名单');
        } else {
            messageApi.error('复制失败，请检查浏览器权限或手动复制');
        }
    };

    // 处理复制排序
    const handleCopySort = async () => {
        const formattedTimeRange = getFormattedTimeRange();
        const tableRows = [];

        tableRows.push([currentConferenceHall.name, '', '']);

        if (formattedTimeRange) {
            tableRows.push(["'" + '时间：' + formattedTimeRange, '', '']);
        }

        if (currentConferenceHall.place) {
            tableRows.push(["'" + '地点：' + currentConferenceHall.place, '', '']);
        }

        tableRows.push(['', '', '']);
        tableRows.push(["'" + '排序', '专家', '单位任职']);

        tableData.forEach(item => {
            const unitText =
                item.workExperiences && item.workExperiences.length > 0
                    ? item.workExperiences
                          .map(exp => {
                              const parts = [];
                              if (exp.unitName && exp.unitName !== 'null') {
                                  parts.push(exp.unitName);
                              }
                              if (exp.unitOffice && exp.unitOffice !== 'null') {
                                  parts.push(exp.unitOffice);
                              }
                              if (exp.unitPost && exp.unitPost !== 'null') {
                                  parts.push(exp.unitPost);
                              }
                              return parts.join('');
                          })
                          .filter(Boolean)
                          .join('、')
                    : '–';
            tableRows.push(["'" + String(item.sort || ''), item.expertName, unitText]);
        });

        const textContent = tableRows.map(row => row.join('\t')).join('\n');

        // 追加排座模式信息（前加一个空行）
        const deskModeText = formatDeskModeRuleText(deskMode);
        const fullText = deskModeText ? `${textContent}\n\n${deskModeText}` : textContent;

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(fullText);
                messageApi.success('已复制（可直接粘贴到Excel）');
                return;
            }
        } catch (err) {
            console.warn('Clipboard API 复制失败，降级使用 execCommand：', err);
        }

        if (fallbackCopyTextToClipboard(fullText)) {
            messageApi.success('已复制（可直接粘贴到Excel）');
        } else {
            messageApi.error('复制失败，请检查浏览器权限或手动复制');
        }
    };

    if (!currentConferenceHall) return null;

    const formattedTimeRange = getFormattedTimeRange();

    return (
        <Drawer
            title="桌签排序"
            open={open}
            onClose={() => {
                if (isDirty) {
                    modal.confirm({
                        title: '排序未保存',
                        content: '变更排序后没有保存，请先保存排序。确定关闭并放弃变更？',
                        centered: true,
                        okText: '放弃变更',
                        okButtonProps: { danger: true },
                        cancelText: '返回保存',
                        onOk: () => {
                            setIsDirty(false);
                            onCancel();
                        },
                    });
                } else {
                    onCancel();
                }
            }}
            size="100%"
            placement="bottom"
            className="seating-arrangement-drawer"
            closable={{ placement: 'end' }}
            footer={
                <div className="seating-drawer-footer">
                    <div className="seating-drawer-footer__meta">
                        <Space>
                            {currentConferenceHall.hallExpertSortsUpdatedAt && <span>排序更新时间：{currentConferenceHall.hallExpertSortsUpdatedAt ? <Text type="warning">{dayjs(currentConferenceHall.hallExpertSortsUpdatedAt).format('YYYY年MM月DD日 HH:mm:ss')}</Text> : <Text type="secondary">&nbsp;</Text>}</span>}
                        </Space>
                    </div>
                    <Space>
                        <Button onClick={handleCopySort}>复制排序数据</Button>
                        <Button type="primary" loading={loading} onClick={handleSaveSort}>
                            保存排序
                        </Button>
                    </Space>
                </div>
            }
        >
            <div className="seating-drawer-body">
                <div className="seating-hall-info">
                    <span className="seating-hall-info__name">
                        {currentConferenceHall.name}
                        {currentConferenceHall.id && <small className="seating-hall-info__id">YHYC#{currentConferenceHall.id}</small>}
                    </span>

                    {formattedTimeRange && (
                        <span className="seating-hall-info__meta">
                            <label>时间：</label>
                            {formattedTimeRange}
                        </span>
                    )}
                    {currentConferenceHall.place && (
                        <span className="seating-hall-info__meta">
                            <label>地点：</label>
                            {currentConferenceHall.place}
                        </span>
                    )}
                </div>

                <div className="seating-main">
                    <div className="seating-experts">
                        <div className="seating-section-header">
                            <div className="seating-section-title">
                                专家排序<span className="seating-section-count">（{tableData.length} 人）</span>
                            </div>
                            <Space size="small">
                                <Button size="small" onClick={handleCopyNameList}>
                                    复制点名表
                                </Button>
                                <Divider type="vertical" />
                                <Button type="primary" ghost size="small" onClick={handleSortByPinyin}>
                                    按姓名拼音排序
                                </Button>
                                <Button type="primary" ghost size="small" onClick={handleSortByAgendaTime}>
                                    按日程时间排序
                                </Button>
                                <Button type="primary" ghost size="small" onClick={handleAutoSort}>
                                    按单位任职排序
                                </Button>
                            </Space>
                        </div>
                        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={tableData.map(item => String(item.key))} strategy={verticalListSortingStrategy}>
                                <Table columns={columns} dataSource={tableData} pagination={false} size="middle" bordered rowKey="key" loading={loading} scroll={{ x: true, y: 'calc(100vh - 280px)' }} components={{ body: { row: SortableTableRow } }} />
                            </SortableContext>
                        </DndContext>
                    </div>

                    <div className="seating-desk-mode">
                        <div className="seating-desk-mode__field">
                            <div className="seating-section-header">
                                <div className="seating-section-title">排座模式</div>
                            </div>
                            <Select
                                allowClear
                                placeholder="请选择排座模式"
                                options={DESK_MODE_OPTIONS}
                                value={deskMode || undefined}
                                onChange={value => {
                                    setDeskMode(value || '');
                                    setIsDirty(true);
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="seating-desk-mode__rules">
                            <DeskModeRuleContent deskMode={deskMode} expertCount={tableData.length} />

                            {deskMode && DEMO_IMAGES[deskMode] && (
                                <div className="seating-desk-mode__demo">
                                    {DEMO_IMAGES[deskMode].type === 'single' ? (
                                        <div className="demo-img-wrap single">
                                            <Image src={DEMO_IMAGES[deskMode].src} alt="排座示意图" preview={{ mask: '查看大图' }} />
                                        </div>
                                    ) : (
                                        <Image.PreviewGroup preview={{ initialScale: 2 }}>
                                            <div className="demo-dual">
                                                <div className={`demo-variant${tableData.length > 0 ? (tableData.length % 2 === 1 ? ' is-active' : ' is-inactive') : ''}`}>
                                                    <div className="demo-label">单数</div>
                                                    <div className="demo-img-wrap">
                                                        <Image src={DEMO_IMAGES[deskMode].odd} alt="单数示意图" preview={{ mask: '查看大图' }} />
                                                    </div>
                                                </div>
                                                <div className={`demo-variant${tableData.length > 0 ? (tableData.length % 2 === 0 ? ' is-active' : ' is-inactive') : ''}`}>
                                                    <div className="demo-label">双数</div>
                                                    <div className="demo-img-wrap">
                                                        <Image src={DEMO_IMAGES[deskMode].even} alt="双数示意图" preview={{ mask: '查看大图' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        </Image.PreviewGroup>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default SeatingArrangementModal;
